import { FC, useState, useMemo, Dispatch, useEffect, useContext, SetStateAction } from 'react'
import { useNavigate } from 'react-router'
import { Box, Button, Select, Checkbox, MenuItem, TextField, Typography } from '@mui/material'
import {
  updateEquipmentById,
  deleteEquipmentById,
  createExerciseEquipment,
  createRequestStationClass,
  createRequestDeviceAntenna,
  deleteRequestStationClassById,
  updateRequestStationClassById,
  deleteRequestDeviceAntennaById,
  updateRequestDeviceAntennaById,
} from '../../../../services/equipment.service'
import SectionLayout from '../SectionLayout'
import { AntennaInfo } from '../AntennaInfo'
import { StationClass } from '../StationClass'
import BookmarkIcon from '@mui/icons-material/Bookmark'
import { useToast } from '../../../../contexts/toast.context'
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder'
import { SelectComponent } from '../../../shared/SelectComponent'
import { SelectWithSearch } from '../../../shared/SearchSelect'
import { SpectrumManagerContext } from '../../../../contexts/spectrum.context'
import { RequestContext } from '../../../../contexts/request.context'
import { RequestEquipmentContext } from '../../../../contexts/request-equipment.context'
import { IPowerUnits } from '../../../../../shared/types/api/spectrum-manager.type'
import { ITier, ITierCommand } from '../../../../../shared/types/api/tier.type'
import { getAllTiers } from '../../../../services/tier.service'

import styles from '../SectionLayout/styles.module.scss'

interface IProps {
  setValue: Dispatch<SetStateAction<number>>
  isLightMode: boolean
  onClose?: () => void
}

const EquipmentSection: FC<IProps> = ({ isLightMode, onClose, setValue }) => {
  const [isDelete, setIsDelete] = useState(false)

  const {
    handleReset,
    selectedRequest,
    requestEquipments,
    frequencyGroups,
    setRequestEquipments,
    getRequestEquipments,
  } = useContext(RequestContext)

  const {
    equipment,
    setEquipment,
    emissionTypes,
    deviceAntennas,
    stationClasses,
    radarTunabilityTypes,
    stationClassDescriptions,
    requestStationClasses,
    requestDeviceAntennas,
    deviceTransmitters,
    setRequestDeviceAntennas,
    setRequestStationClasses,
  } = useContext(RequestEquipmentContext)

  const { showToast } = useToast()
  const navigate = useNavigate()

  const { devices, numberOfStations } = useContext(SpectrumManagerContext)

  const [tiers, setTiers] = useState<ITier[]>([])
  const f207Tiers = useMemo(() => tiers.filter((t) => t.level === 'f207'), [tiers])

  const handleClose = () => {
    setIsDelete(false)
  }

  const handleAdd = async () => {
    if (selectedRequest) {
      const data = await createExerciseEquipment(selectedRequest.id)

      if (data.success && data.data) {
        setRequestEquipments([...requestEquipments, data.data])
        showToast('success', 'Equipment created successfully')
      }
    }
  }

  const handleSave = async (isNavigating?: boolean) => {
    if (equipment) {
      const data = await updateEquipmentById(equipment.id, equipment)

      if (data.success && data.data) {
        if (!isNavigating) {
          showToast('success', 'Equipment updated successfully')
        }

        for (const rsc of requestStationClasses) {
          await updateRequestStationClassById(rsc.id, rsc)
        }

        for (const deviceAntenna of requestDeviceAntennas) {
          await updateRequestDeviceAntennaById(deviceAntenna.id, deviceAntenna)
        }
        await getRequestEquipments()

        return true
      }
    }
    handleClose()
  }

  const handleDelete = async () => {
    if (equipment) {
      const data = await deleteEquipmentById(equipment.id)

      if (data.success) {
        setRequestEquipments(requestEquipments.filter((elem) => elem.id !== equipment.id))
        showToast('success', 'Equipment removed successfully')
        setEquipment(null)
      }
    }
    handleClose()
  }

  const handleEquipmentChange = (key: string, value: string | number | boolean | null) => {
    if (equipment) {
      setEquipment({ ...equipment, [key]: value })
    }
  }

  const handleAddStationClass = async () => {
    if (equipment) {
      const data = await createRequestStationClass(equipment.id)

      if (data.success && data.data) {
        const newStationClass = {
          ...data.data,
          power_units: 'W' as IPowerUnits,
        }

        setRequestStationClasses([...requestStationClasses, newStationClass])
      }
    }
  }

  const handleAntennaAdd = async () => {
    if (equipment) {
      const data = await createRequestDeviceAntenna(equipment.id)

      if (data.success && data.data) {
        setRequestDeviceAntennas([...requestDeviceAntennas, data.data])
      }
    }
  }

  const handleDeleteStationClass = async (id: number) => {
    const data = await deleteRequestStationClassById(id)

    if (data.success) {
      setRequestStationClasses(requestStationClasses.filter((stationClass) => stationClass.id !== id))
    }
  }

  const handleDeleteDeviceAntenna = async (id: number) => {
    const data = await deleteRequestDeviceAntennaById(id)

    if (data.success) {
      setRequestDeviceAntennas(requestDeviceAntennas.filter((deviceAntenna) => deviceAntenna.id !== id))
    }
  }

  const handleStationClassChange = (id: number, key: string, value: string | number) => {
    const updatedStationClasses = requestStationClasses.map((stationClass) =>
      stationClass.id === id ? { ...stationClass, [key]: value } : stationClass,
    )

    setRequestStationClasses(updatedStationClasses)
  }

  const handleAntennaChange = (id: number, key: string, value: string | number) => {
    const updatedDeviceAntennas = requestDeviceAntennas.map((deviceAntenna) =>
      deviceAntenna.id === id ? { ...deviceAntenna, [key]: value } : deviceAntenna,
    )

    setRequestDeviceAntennas(updatedDeviceAntennas)
  }

  const handleNavigate = async () => {
    const result = await handleSave(true)

    if (onClose) {
      onClose()

      return
    }

    if (result) {
      setValue(0)
      handleReset()
      setEquipment(null)

      showToast('success', 'Your request is submitted successfully!')
      navigate('/frequency-request-form')
    } else {
      showToast('error', 'Failed to submit your request')
    }
  }

  useEffect(() => {
    const fetchTiers = async () => {
      const data = await getAllTiers()
      if (data.success && data.data) {
        setTiers(data.data.tiers)
      }
    }
    fetchTiers()
  }, [])

  useEffect(() => {
    if (equipment && !equipment?.request_frequency_group_id && frequencyGroups.length) {
      setEquipment({
        ...equipment,
        request_frequency_group_id: frequencyGroups[0].id,
      })
    }
  }, [equipment])

  return (
    <SectionLayout
      sectionTitle="Equipment"
      btnText="Add Equipment"
      handleAdd={handleAdd}
      isDelete={isDelete}
      handleNavigate={handleNavigate}
      handleClose={handleClose}
      handleSave={handleSave}
      handleDelete={handleDelete}
      setIsDelete={setIsDelete}
      setValue={setValue}
      deleteText={'Are you sure you want to delete this equipment?'}
      headerItems={
        <>
          <SelectWithSearch
            width={300}
            value={frequencyGroups
              .map((fg) => ({
                label: `${fg.platform} (${fg.start_freq}${fg.start_indicator}-${fg.end_freq}${fg.end_indicator})`,
                value: fg.id,
              }))
              .find((elem) => elem.value === equipment?.request_frequency_group_id)}
            label="Frequency"
            handleChange={(value) => handleEquipmentChange('request_frequency_group_id', Number(value))}
            options={frequencyGroups.map((fg) => ({
              value: fg.id,
              label: `${fg.platform} (${fg.start_freq}${fg.start_indicator}-${fg.end_freq}${fg.end_indicator})`,
            }))}
          />
        </>
      }
      isSelected={!!equipment}
      sidebarItems={
        <Box className={styles.detailedContainer}>
          {requestEquipments.map((elem) => (
            <Box
              key={elem.id}
              onClick={() => setEquipment(elem)}
              className={
                elem.id === equipment?.id
                  ? isLightMode
                    ? styles.item
                    : styles.selectedItem
                  : isLightMode
                    ? styles.lightItem
                    : styles.item
              }
            >
              {elem.id === equipment?.id ? (
                <BookmarkIcon className={styles.circle} />
              ) : (
                <BookmarkBorderIcon className={styles.circle} />
              )}
              <Box className={styles.details}>
                <Typography>Name:</Typography>
                <Typography>{elem.equipment_name}</Typography>
              </Box>
              <Box className={styles.details}>
                <Typography>Frequency:</Typography>
                <Typography>
                  {elem.start_freq} {elem.start_indicator}
                </Typography>
              </Box>
              <Box className={styles.details}>
                <Typography>Location:</Typography>
                <Typography>{elem.transmitter_location}</Typography>
              </Box>
              <Box className={styles.details}>
                <Typography>Receiving Unit:</Typography>
                <Typography>{elem.receiving_operating_unit_name}</Typography>
              </Box>
              <Box className={styles.details}>
                <Typography>Station Config:</Typography>
                <Typography>{elem.station_class_type}</Typography>
              </Box>
              <Box className={styles.details}>
                <Typography>Major Fn:</Typography>
                <Typography>{elem.major_fn_name}</Typography>
              </Box>
              <Box className={styles.details}>
                <Typography>Detailed Fn:</Typography>
                <Typography>{elem.detailed_fn_name}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      }
      fieldItems={
        equipment
          ? [
              <Box className={styles.section}>
                <Box className={styles.detailsSubSection}>
                  <Box className={styles.fieldContainer}>
                    <Typography className={styles.label}>Send/Receive:</Typography>
                    <Select
                      className={styles.field}
                      onChange={(evt) => handleEquipmentChange('send_receive', evt.target.value || 'Send and Receive')}
                      value={equipment.send_receive}
                    >
                      {['Send and Receive', 'Send Only', 'Receive Only'].map((elem) => (
                        <MenuItem key={elem} value={elem}>
                          {elem}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                  <Box className={styles.fieldContainer}>
                    <Typography className={styles.label}>Device:</Typography>
                    <SelectWithSearch
                      width={350}
                      value={devices
                        .map((device) => ({
                          label: device.model,
                          value: device.id,
                        }))
                        .find((elem) => elem.value === equipment.device_id)}
                      label="Devices"
                      handleChange={(value) => handleEquipmentChange('device_id', Number(value))}
                      options={devices.map((device) => ({
                        value: device.id,
                        label: device.model,
                      }))}
                    />
                  </Box>
                  <Box className={styles.fieldContainer}>
                    <Typography className={styles.label}>Number of Stations:</Typography>
                    <SelectComponent
                      label="Number of Stations"
                      value={equipment.number_of_stations_id}
                      className={styles.field}
                      items={numberOfStations.map((numberOfStation) => ({
                        value: numberOfStation.id,
                        displayValue: numberOfStation.code,
                      }))}
                      handleChange={(value) => handleEquipmentChange('number_of_stations_id', Number(value))}
                    />
                  </Box>
                </Box>
                {requestStationClasses.map((stationClass) => (
                  <StationClass
                    key={stationClass.id}
                    stationClass={stationClass}
                    handleDelete={handleDeleteStationClass}
                    handleChange={handleStationClassChange}
                    emissionTypes={emissionTypes}
                    stationClasses={stationClasses}
                    f207Tiers={f207Tiers}
                    stationClassDescriptions={stationClassDescriptions}
                    deviceTransmitters={deviceTransmitters}
                  />
                ))}
                <Button variant="outlined" className={styles.addFreq} onClick={handleAddStationClass}>
                  Add a station class
                </Button>
                {requestDeviceAntennas.map((deviceAntenna) => (
                  <AntennaInfo
                    key={deviceAntenna.id}
                    antennaInfo={deviceAntenna}
                    deviceAntennas={deviceAntennas}
                    handleDelete={handleDeleteDeviceAntenna}
                    handleChange={handleAntennaChange}
                  />
                ))}
                <Button variant="outlined" className={styles.addFreq} onClick={handleAntennaAdd}>
                  Add an Antenna
                </Button>
              </Box>,
              <Box className={styles.section}>
                <Box className={styles.detailsSubSection}>
                  <Box className={styles.fieldContainer}>
                    <Box></Box>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <Typography>Show Radar Characteristics: </Typography>
                      <Checkbox
                        checked={equipment.is_radar_chars}
                        onChange={(_, checked) => handleEquipmentChange('is_radar_chars', checked)}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>,
              equipment.is_radar_chars ? (
                <Box className={styles.section}>
                  <Box className={styles.titleContainer}>
                    <Typography className={styles.title}>Radar Characteristics</Typography>
                  </Box>
                  <Box className={styles.subSection}>
                    <Box className={styles.fieldContainer}>
                      <Typography className={styles.label}>Tunability:</Typography>
                      <SelectComponent
                        label="Tunability"
                        value={equipment.radar_tunability_type_id}
                        className={styles.field}
                        items={radarTunabilityTypes.map((tunability) => ({
                          value: tunability.id,
                          displayValue: tunability.name,
                        }))}
                        handleChange={(value) => handleEquipmentChange('radar_tunability_type_id', Number(value))}
                      />
                    </Box>
                    <Box className={styles.fieldContainer}>
                      <Typography className={styles.label}>Pulse duration (Width):</Typography>
                      <TextField
                        label="Pulse duration"
                        value={equipment.radar_pulse_duration}
                        className={styles.field}
                        onChange={(evt) => handleEquipmentChange('radar_pulse_duration', Number(evt.target.value))}
                      />
                    </Box>
                    <Box className={styles.fieldContainer}>
                      <Typography className={styles.label}>Pulse repetition rate:</Typography>
                      <TextField
                        label="Pulse repetition rate"
                        value={equipment.radar_pulse_repetition_rate}
                        className={styles.field}
                        onChange={(evt) =>
                          handleEquipmentChange('radar_pulse_repetition_rate', Number(evt.target.value))
                        }
                      />
                    </Box>
                    <Box className={styles.fieldContainer}>
                      <Typography className={styles.label}>Intermediate Frequency:</Typography>
                      <Box className={styles.field} style={{ display: 'flex', gap: 10 }}>
                        <TextField
                          value={equipment.radar_intermediate_frequency}
                          fullWidth
                          label="Intermediate Frequency"
                          className={styles.field}
                          onChange={(evt) =>
                            handleEquipmentChange('radar_intermediate_frequency', Number(evt.target.value))
                          }
                        />
                        <Select
                          value={equipment.radar_intermediate_frequency_indicator}
                          onChange={(evt) =>
                            handleEquipmentChange('radar_intermediate_frequency_indicator', evt.target.value)
                          }
                        >
                          {['K', 'M', 'G', 'T'].map((elem) => (
                            <MenuItem key={elem} value={elem}>
                              {elem}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                    </Box>
                    <Box className={styles.fieldContainer}>
                      <Typography className={styles.label}>Sidelobe Suppression:</Typography>
                      <SelectComponent
                        label="Sidelobe Suppression"
                        value={equipment.radar_sidelobe_suppression}
                        className={styles.field}
                        items={['Y', 'N'].map((elem) => ({
                          value: elem,
                          displayValue: elem,
                        }))}
                        handleChange={(value) => handleEquipmentChange('radar_sidelobe_suppression', Number(value))}
                      />
                    </Box>
                    <Box className={styles.fieldContainer}>
                      <Typography className={styles.label}>Power:</Typography>
                      <TextField
                        label="Power"
                        value={equipment.radar_power}
                        className={styles.field}
                        onChange={(evt) => handleEquipmentChange('radar_power', Number(evt.target.value))}
                      />
                    </Box>
                  </Box>
                </Box>
              ) : (
                <></>
              ),
            ]
          : []
      }
    />
  )
}

export default EquipmentSection
